<section id="usage_reporting_head" class="side-padding" aria-labelledby="policy_h2">
  <h2 id="policy_h2">Usage Reporting</h2>
  <p id="user_info" *ngIf="isCustomerAdmin">
    As an identifed "Customer Admin" user, you are seeing data aggrigated across <em>all</em>
    {{ totalCourseCount }} courses, for <em>all</em> {{ totalUniqueUsers }} users, for {{ customer }}.
  </p>
  <p id="user_info" *ngIf="!isCustomerAdmin">
    The data is for all your own actions, <em>plus</em> the usage for any and all courses you have been an
    <code>Instructor/Tutor</code> on (as seen by the service).
    <span *ngIf="totalCourseCount > 0 && totalUniqueUsers > 1"
      >It covers {{ totalCourseCount }} courses, and {{ totalUniqueUsers }} users</span
    >
    <span *ngIf="totalCourseCount < 1 && totalUniqueUsers > 1">It covers {{ totalUniqueUsers }} users</span>
    <span *ngIf="totalCourseCount > 0 && totalUniqueUsers < 2">It covers {{ totalCourseCount }} courses</span>
  </p>
  <p>
    These statistics cover the past year (data found from {{ startDate }}<span *ngIf="endDate"> to {{ endDate }}</span
    >)
  </p>
  <p class="download_button">
    <a href="/api/usagestats/raw" role="button" class="raw_download_button" rel="noopener">Download the raw data</a>
  </p>
</section>
<section id="monthly_summary" class="side-padding" aria-labelledby="h3_monthly_summary">
  <h3 id="h3_monthly_summary">Monthly summary data</h3>
  <p>This shows the number of logins (cumulative and unique), and the number of notebook servers started.</p>
  <canvas
    baseChart
    [data]="monthlyChartData"
    [options]="monthlyChartOptions"
    [type]="lineChartType"
    aria-label="Line graph showing usage by month for the past year"></canvas>
  <table id="monthly_summary_table" aria-label="Table listing usage by month for the past year">
    <thead>
      <tr aria-label="Column headers">
        <th scope="col">Timeframe</th>
        @for (d of monthlyChartData.datasets; track d; let i = $index) {
          <th scope="col">{{ d.label }}</th>
        }
      </tr>
    </thead>
    <tbody>
      @for (label of monthlyChartData.labels; track label; let i = $index) {
        <tr [attr.aria-labelledby]="'monthly_row_' + i">
          <th scope="row" [id]="'monthly_row_' + i">{{ label }}</th>
          @for (d of monthlyChartData.datasets; track d; let j = $index) {
            <td [id]="'monthly_row_' + i + '_' + j">
              {{ d && d.data[i] }}
            </td>
          }
        </tr>
      }
    </tbody>
  </table>
  <p>
    We note that the number of logins does not need to corralate to the number of notebook servers started: users may
    login, then not use the system; or may switch notebook servers within the same session.
  </p>
</section>

<section id="annual_summary" aria-labelledby="h3_annual_summary" class="side-padding">
  <h3 id="h3_annual_summary">Number of notebook servers over the year</h3>
  <p>This shows the relative number of notebook servers started over the past year.</p>
  <div class="gridify">
    <div class="cellA">
      <canvas
        baseChart
        [data]="doughnutChartData"
        [options]="doughnutChartOptions"
        [type]="doughnutChartType"
        aria-label="Donut graph of the count of notebooks started"></canvas>
    </div>
    <div class="cellB">
      <table id="annual_summary_table" aria-label="Table listing notebooks and the number of times each was used">
        <thead>
          <tr aria-label="Column headers">
            <th scope="col">Notebook</th>
            <th scope="col">Count</th>
          </tr>
        </thead>
        <tbody>
          @for (item of sortedDonutTableData; track item; let i = $index) {
            <tr [attr.aria-labelledby]="'donut_row_' + i">
              <th scope="row" [id]="'donut_row_' + i">{{ item.label }}</th>
              <td [id]="'donut_row_' + i">
                {{ item.value | number: '1.0-0' }}
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
</section>

<section id="popularity_summary" aria-labelledby="h3_start_popularity" class="side-padding">
  <h3 id="h3_start_popularity">Popularity over time</h3>
  <p>The popular times to <em>start</em> Notebook Servers (over the last year).</p>
  <canvas
    baseChart
    [data]="timeOfDayChartData"
    [options]="timeOfDayChartOptions"
    [type]="barChartType"
    aria-label="Bar graph showing notebook start times"></canvas>
</section>

<section id="course_counts" aria-labelledby="h3_course_counts" class="side-padding">
  <h3 id="h3_course_counts">List of Courses</h3>
  <p>These two charts list {{ totalCourseCount }} courses.</p>
  <ul>
    <li>
      The first is the number of unique users on each course, with the X-axis (courses) sorted by the number of unique
      users.
    </li>
    <li>
      The second is a <em>stacked</em> list of the number of notebooks started on each course. Again, the X-axis
      (courses) sorted by the total number of notebooks started
    </li>
  </ul>
  <p>The charts are individually <em>zoomable</em>, and can be <em>panned</em> to explore.</p>
  <canvas
    baseChart
    [data]="usersPerCourseChartData"
    [options]="usersPerCourseChartOptions"
    [type]="barChartType"
    aria-label="Bar graph unique users per course"></canvas>
  <canvas
    baseChart
    [data]="notebooksPerCourseChartData"
    [options]="notebooksPerCourseChartOptions"
    [type]="barChartType"
    aria-label="Stacked Bar graph total number of notebooks started per course"></canvas>
</section>
